import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/HttpClient';
import dayjs from 'dayjs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Trash2, Receipt, TrendingUp } from 'lucide-react';
import { cn } from '../lib/utils';

interface ExpenseTrackerProps {
  propertyId: string;
}

interface Expense {
  id: string;
  description: string;
  amount: number;
  date: string;
}

interface ExtraProfit {
  id: string;
  description: string;
  amount: number;
  date: string;
}

type ActiveTab = 'expenses' | 'profits';

const ExpenseTracker: React.FC<ExpenseTrackerProps> = ({ propertyId }) => {
  const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().format('YYYY-MM'));
  const [activeTab, setActiveTab] = useState<ActiveTab>('expenses');

  const generateMonthOptions = () => {
    const months = [];
    let date = dayjs();
    for (let i = 0; i < 13; i++) {
      months.push(date.format('YYYY-MM'));
      date = date.subtract(1, 'month');
    }
    return months;
  };

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(dayjs().format('YYYY-MM-DD'));

  const [extraProfits, setExtraProfits] = useState<ExtraProfit[]>([]);
  const [totalExtraProfits, setTotalExtraProfits] = useState(0);
  const [profitDescription, setProfitDescription] = useState('');
  const [profitAmount, setProfitAmount] = useState('');
  const [profitDate, setProfitDate] = useState(dayjs().format('YYYY-MM-DD'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const client = await apiClient;
      const [expData, profData] = await Promise.all([
        client.get<{ expenses: Expense[]; total_expenses: number }>(
          `/api/pg_tenant/property/${propertyId}/expenses/?month=${selectedMonth}`
        ),
        client.get<{ extra_profits: ExtraProfit[]; total_extra_profits: number }>(
          `/api/pg_tenant/property/${propertyId}/extra-profits/?month=${selectedMonth}`
        ),
      ]);
      setExpenses(expData.expenses);
      setTotalExpenses(expData.total_expenses);
      setExtraProfits(profData.extra_profits);
      setTotalExtraProfits(profData.total_extra_profits);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [propertyId, selectedMonth]);

  const handleAddExpense = async () => {
    try {
      const client = await apiClient;
      await client.post(`/api/pg_tenant/property/${propertyId}/expenses/`, {
        property_id: propertyId, description, amount, date: expenseDate,
      });
      fetchData();
      setDescription(''); setAmount(''); setExpenseDate(dayjs().format('YYYY-MM-DD'));
    } catch (e: any) { setError(e.message); }
  };

  const handleDeleteExpense = async (id: string) => {
    try {
      const client = await apiClient;
      await client.delete(`/api/pg_tenant/property/${propertyId}/expenses/${id}/`);
      fetchData();
    } catch (e: any) { setError(e.message); }
  };

  const handleAddProfit = async () => {
    try {
      const client = await apiClient;
      await client.post(`/api/pg_tenant/property/${propertyId}/extra-profits/`, {
        property_id: propertyId, description: profitDescription, amount: profitAmount, date: profitDate,
      });
      fetchData();
      setProfitDescription(''); setProfitAmount(''); setProfitDate(dayjs().format('YYYY-MM-DD'));
    } catch (e: any) { setError(e.message); }
  };

  const handleDeleteProfit = async (id: string) => {
    try {
      const client = await apiClient;
      await client.delete(`/api/pg_tenant/property/${propertyId}/extra-profits/${id}/`);
      fetchData();
    } catch (e: any) { setError(e.message); }
  };

  if (loading) return <div className="flex justify-center items-center h-40"><p className="text-muted-foreground">Loading...</p></div>;
  if (error)   return <p className="text-red-500">Error: {error}</p>;

  const isExpenses = activeTab === 'expenses';

  return (
    <div className="space-y-5">

      {/* ── Top bar: title + month ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-foreground">Expense Tracker</h2>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[150px] bg-background">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent className="bg-card border z-50">
            {generateMonthOptions().map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ── Toggle ── */}
      <div className="flex rounded-xl overflow-hidden border border-primary/30 w-fit">
        <button
          onClick={() => setActiveTab('expenses')}
          className={cn(
            'flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-all duration-200',
            isExpenses
              ? 'bg-primary text-white'
              : 'bg-background text-muted-foreground hover:bg-primary/5'
          )}
        >
          <Receipt className="h-4 w-4" />
          Monthly Expenses
          {totalExpenses > 0 && (
            <span className={cn(
              'ml-1 text-xs px-1.5 py-0.5 rounded-full font-bold',
              isExpenses ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'
            )}>
              {expenses.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('profits')}
          className={cn(
            'flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-all duration-200',
            !isExpenses
              ? 'bg-primary text-white'
              : 'bg-background text-muted-foreground hover:bg-primary/5'
          )}
        >
          <TrendingUp className="h-4 w-4" />
          Extra Profits
          {totalExtraProfits > 0 && (
            <span className={cn(
              'ml-1 text-xs px-1.5 py-0.5 rounded-full font-bold',
              !isExpenses ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'
            )}>
              {extraProfits.length}
            </span>
          )}
        </button>
      </div>

      {/* ── Add form + total ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Form card */}
        <div className="lg:col-span-3 bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-primary">
            <h3 className="text-primary-foreground font-semibold">
              {isExpenses ? 'Add New Expense' : 'Add Extra Profit'}
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {isExpenses ? (
              <>
                <Input placeholder="Description *" value={description}
                  onChange={e => setDescription(e.target.value)} className="bg-background" />
                <Input placeholder="Amount" type="number" value={amount}
                  onChange={e => setAmount(e.target.value)} className="bg-background" />
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">Date *</label>
                  <Input type="date" value={expenseDate} max={dayjs().format('YYYY-MM-DD')}
                    onChange={e => setExpenseDate(e.target.value)} className="bg-background" />
                </div>
                <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  onClick={handleAddExpense} disabled={!description || !amount || !expenseDate}>
                  ADD EXPENSE
                </Button>
              </>
            ) : (
              <>
                <Input placeholder="Description * (e.g. 1 day temp stay)" value={profitDescription}
                  onChange={e => setProfitDescription(e.target.value)} className="bg-background" />
                <Input placeholder="Amount" type="number" value={profitAmount}
                  onChange={e => setProfitAmount(e.target.value)} className="bg-background" />
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">Date *</label>
                  <Input type="date" value={profitDate} max={dayjs().format('YYYY-MM-DD')}
                    onChange={e => setProfitDate(e.target.value)} className="bg-background" />
                </div>
                <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  onClick={handleAddProfit} disabled={!profitDescription || !profitAmount || !profitDate}>
                  ADD PROFIT
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Total card */}
        <div className="lg:col-span-2 bg-primary rounded-xl p-5 flex flex-col justify-center">
          <p className="text-primary-foreground/70 text-sm font-medium mb-1">
            {isExpenses ? 'Total Expenses' : 'Total Extra Profits'}
          </p>
          <p className="text-primary-foreground text-3xl font-bold">
            {isExpenses ? '' : '+'}₹{Number(isExpenses ? totalExpenses : totalExtraProfits)
              .toLocaleString('en-IN', { minimumFractionDigits: 0 })}
          </p>
          <p className="text-primary-foreground/50 text-xs mt-2">
            {isExpenses ? expenses.length : extraProfits.length} record(s) · {dayjs(selectedMonth).format('MMM YYYY')}
          </p>
        </div>
      </div>

      {/* ── List ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {isExpenses ? (
          expenses.length > 0 ? expenses.map(expense => (
            <div key={expense.id} className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="bg-primary px-4 py-3 flex items-center justify-between">
                <h4 className="text-primary-foreground font-medium truncate pr-2">{expense.description}</h4>
                <Button variant="ghost" size="icon"
                  className="h-7 w-7 shrink-0 text-primary-foreground/70 hover:text-primary-foreground hover:bg-white/10"
                  onClick={() => handleDeleteExpense(expense.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4 space-y-1">
                <p className="text-sm text-muted-foreground">
                  Amount: <span className="text-foreground font-semibold">₹{Number(expense.amount).toLocaleString('en-IN')}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Date: <span className="text-foreground font-medium">{dayjs(expense.date).format('DD MMM YYYY')}</span>
                </p>
              </div>
            </div>
          )) : (
            <div className="col-span-full text-center py-10 text-muted-foreground">
              No expenses recorded for {dayjs(selectedMonth).format('MMMM YYYY')}.
            </div>
          )
        ) : (
          extraProfits.length > 0 ? extraProfits.map(profit => (
            <div key={profit.id} className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="bg-primary px-4 py-3 flex items-center justify-between">
                <h4 className="text-primary-foreground font-medium truncate pr-2">{profit.description}</h4>
                <Button variant="ghost" size="icon"
                  className="h-7 w-7 shrink-0 text-primary-foreground/70 hover:text-primary-foreground hover:bg-white/10"
                  onClick={() => handleDeleteProfit(profit.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4 space-y-1">
                <p className="text-sm text-muted-foreground">
                  Amount: <span className="text-primary font-semibold">+₹{Number(profit.amount).toLocaleString('en-IN')}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Date: <span className="text-foreground font-medium">{dayjs(profit.date).format('DD MMM YYYY')}</span>
                </p>
              </div>
            </div>
          )) : (
            <div className="col-span-full text-center py-10 text-muted-foreground">
              No extra profits recorded for {dayjs(selectedMonth).format('MMMM YYYY')}.
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ExpenseTracker;
